import { sumBy } from 'lodash';
import { msToD } from '../../lib/time';
import { generateClientId, type ClientId } from '../core/ClientId';
import type { TimeData } from '../core/TimeData';
import {
  createBoundaryVisitResolver,
  createBoundaryVisitResolverFromLinks,
  excludeDryDockPeriods,
  getBoundaryPairPreviousPortCallId,
  isCargoEventTimeData,
  isPortCallEvent,
  isPortCallEventTimeData,
  isLoadEventTimeData,
  isDischargeEventTimeData,
  sumBunkerGrades,
} from '../core/TimeData';
import type { EventTimeline } from '../core/Timelines';
import type { Distances } from '../distances/context';
import type { PnlData } from '../hooks/usePnlData';
import type { Cost } from './costs/costs';
import { getExtraCostsForLoadedAmount } from './costs/extraCost';
import type {
  GetIncomeForLoadedAmount,
  Income,
  IncomeParam,
} from './income/income';
import { getTDIncomeAndMassDistance } from './income/income';
import { getEventsFinancialBounds, getTimelineBounds } from './timelineBounds';
import type { LoadedAmount } from './loadedAmounts';
import { DefaultMap, mapGetFirstEntry } from '@seaber/common/lib/collections';
import { filteredMap, NONE, setUnion } from '../../lib/util';
import { convertToDefaultCurrency } from '../../state/zustand';
import { getEventShipmentId } from './shipment';
import type { ScenarioOrder } from '../../lib/planningData';
import type { Pnl } from '../pnl/ui/context';
import type { CoasById } from '../timeline/utils/TimelineFreightCost';
import type {
  ScenarioShipmentExtraFreight,
  ScenarioShipmentFreight,
} from '../Scenario';
import { calculateTimelineFreightCosts } from '../timeline/utils/TimelineFreightCost';
import { calculateBreakEvenFreight } from '../pnl/utils/breakEvenFreight';
import type { LocationId } from '@seaber/common/core/entries';

/**
 * @param income
 * @param days
 * @param costs Total costs, excluding tc costs
 */
export const calculateTce = (income: number, costs: number, days: number) =>
  days > 0 ? (income - costs) / days : 0;

export interface VoyageTce {
  value: number;
  params: IncomeParam[];
}

export const getTimelineTce = (
  timeline: EventTimeline,
  costs: Cost[],
  income: Income[],
  euEtsCost: number,
  fuelEuCosts: number,
): VoyageTce | null => {
  const totalCosts =
    sumBy(costs, (cost) => cost.value ?? 0) + euEtsCost + fuelEuCosts;
  const totalIncome = sumBy(income, (income) => income.value ?? 0);
  const { startTime, endTime } = getTimelineBounds(timeline);

  if (!startTime || !endTime) {
    console.error(
      'getTimelineTce error: Start / end time not found for timeline',
      startTime,
      endTime,
    );

    return null;
  }

  const durationInDays = msToD(endTime - startTime);

  const tce = calculateTce(totalIncome, totalCosts, durationInDays);

  return {
    value: tce,
    params: [
      { name: 'Total income', value: totalIncome },
      { name: 'Total costs', value: totalCosts },
      { name: 'Duration (d)', value: durationInDays },
    ],
  };
};

export type TceComputationOptions = {
  firstIndex?: number;
  lastIndex?: number;
  euEtsCostEnabled: boolean;
  fuelEuCostEnabled: boolean;
  // TODO: outgoingCoaId seems to be  unused everywhere. remove or consider in implementation?
  outgoingCoaId: number | null;
  pnlData: PnlData | null;
  portCallCostSharingDetails: CostSharingDetailsByPortCall;
  /** Pre-computed PnL port costs per port call. When provided, these are used
   * instead of td.costs.portCostEstimate + VC lookup, ensuring Timeline matches PnL. */
  pnlPortCostByPortCallId?: Map<ClientId, number>;
  /** Timeline the voyage is shown on. On 'coa'/'spot' timelines the executing
   * vessel belongs to the shipowner, so its operating cost (bunkers, TC hire) is
   * not the charterer's and is excluded from the voyage cost. */
  timelineType?: EventTimeline['type'];
  /** COA contracts by id, for pricing COA cargo off the contract. */
  coasById?: CoasById;
  /** Scenario freight entered against the shipment, which replaces the cargo's
   * own rates. The voyage box label and the P&L panel both apply it. */
  shipmentFreightByShipmentClientId?: Map<ClientId, ScenarioShipmentFreight>;
  shipmentExtraFreightsByShipmentClientId?: Map<
    ClientId,
    ScenarioShipmentExtraFreight[]
  >;
};

export type PortCallCostSharingDetails = {
  /** First loaded shipment of the currently carried ones */
  firstShipmentOnBoard: ClientId | null;
  /** All shipments that are carried/loaded/discharged during the port call */
  shipmentsOnBoard: Set<ClientId>;
  /** All shipments that have cargo operations during the port call */
  shipmentsWithCargoOperations: Set<ClientId>;
};

function createEmptyPortCallCostSharingDetails(): PortCallCostSharingDetails {
  return {
    firstShipmentOnBoard: null,
    shipmentsOnBoard: new Set(),
    shipmentsWithCargoOperations: new Set(),
  };
}

export type CostSharingDetailsByPortCall = Map<
  ClientId,
  PortCallCostSharingDetails
>;

export const MISSING_SHIPMENT_ID = generateClientId();

const loggedMissingShipmentIds = new Set<number>();
export function logMissingShipmentId(order: ScenarioOrder) {
  if (!loggedMissingShipmentIds.has(order.id)) {
    console.error('No shipment id for assigned order', order);
    loggedMissingShipmentIds.add(order.id);
  }
}

/**
 * Get cost sharing details for port calls from time data.
 *
 * NOTE: should be given all vessel time datas as input so
 * proper sharing details can be determined. Passing a partial
 * timeline can cause issues.
 */
export function getCostSharingDetailsForPortCalls(
  timeData: TimeData[],
): CostSharingDetailsByPortCall {
  const portCallDetailsByPortCallId: CostSharingDetailsByPortCall = new Map();

  let currentPortCallId = null;
  /** Tracks the order in which shipments are loaded (Map keys maintain their order) */
  const shipmentIdByLoadedOrder = new Map<number, ClientId>();

  /** Consecutive-COA boundary-pair links (load side → discharge side), for
   * visit-wide cost sharing (SEABER-6784) */
  const previousIdByPortCallId = new Map<ClientId, ClientId>();

  let currentPortCallDetails: PortCallCostSharingDetails =
    createEmptyPortCallCostSharingDetails();

  for (const td of timeData) {
    const portCallId = td.event.portCallId;

    if (isPortCallEvent(td.event)) {
      const previousId = getBoundaryPairPreviousPortCallId(td.event);
      if (previousId !== null) {
        previousIdByPortCallId.set(td.event.portCallId, previousId);
      }
    }

    // Check if port call has changed
    if (portCallId && portCallId !== currentPortCallId) {
      // Save previous port call details
      if (currentPortCallId) {
        // Record all shipments that were carried during the port call and add potentially loaded/discharged ones
        const allShipments = new Set(shipmentIdByLoadedOrder.values());

        currentPortCallDetails.shipmentsWithCargoOperations.forEach(
          (shipmentId) => {
            allShipments.add(shipmentId);
          },
        );

        currentPortCallDetails.shipmentsOnBoard = allShipments;

        portCallDetailsByPortCallId.set(
          currentPortCallId,
          currentPortCallDetails,
        );
      }

      // Init new port call details
      currentPortCallId = portCallId;
      currentPortCallDetails = createEmptyPortCallCostSharingDetails();

      // If we arrive at a new port call with carried shipments, record which of them was loaded first
      if (shipmentIdByLoadedOrder.size > 0) {
        const firstEntry = mapGetFirstEntry(shipmentIdByLoadedOrder);
        if (firstEntry) {
          currentPortCallDetails.firstShipmentOnBoard = firstEntry[1];
        }
      }
    }

    if (isCargoEventTimeData(td)) {
      const shipmentId = getEventShipmentId(td.event) ?? MISSING_SHIPMENT_ID;

      // Shipment id is expected to be available for an order on the timeline
      if (shipmentId === MISSING_SHIPMENT_ID) {
        logMissingShipmentId(td.event.order);
      }

      if (isLoadEventTimeData(td)) {
        shipmentIdByLoadedOrder.set(td.event.order.id, shipmentId);

        // If we arrived to the port call without loaded shipments record this as the first one
        if (currentPortCallDetails.firstShipmentOnBoard === null) {
          currentPortCallDetails.firstShipmentOnBoard = shipmentId;
        }
      }

      if (isDischargeEventTimeData(td)) {
        if (shipmentIdByLoadedOrder.has(td.event.order.id)) {
          shipmentIdByLoadedOrder.delete(td.event.order.id);
        } else {
          console.error(
            'No matching load event found for order',
            td.event.order.id,
          );
        }
      }

      currentPortCallDetails.shipmentsWithCargoOperations.add(shipmentId);
    }
  }

  // Save the final port call details after loop completes
  if (currentPortCallId) {
    const allShipments = new Set(shipmentIdByLoadedOrder.values());
    currentPortCallDetails.shipmentsWithCargoOperations.forEach(
      (shipmentId) => {
        allShipments.add(shipmentId);
      },
    );
    currentPortCallDetails.shipmentsOnBoard = allShipments;

    portCallDetailsByPortCallId.set(currentPortCallId, currentPortCallDetails);
  }

  // A boundary-pair visit is one shared visit for cost purposes: every member
  // (chains of pairs compose into one visit) carries the visit-wide sharing
  // set, while cargo operations stay per side.
  const visitResolver = createBoundaryVisitResolverFromLinks(
    previousIdByPortCallId,
  );
  for (const memberIds of visitResolver.pairedVisits) {
    const memberDetails = filteredMap(
      [...memberIds],
      (id) => portCallDetailsByPortCallId.get(id) ?? NONE,
    );
    if (memberDetails.length < 2) continue;
    const shared = setUnion(memberDetails.map((d) => d.shipmentsOnBoard));
    for (const details of memberDetails) {
      details.shipmentsOnBoard = shared;
    }
  }

  return portCallDetailsByPortCallId;
}

export type TceResult = ReturnType<typeof computeTceForTimeData>;

/** Compute TCE for a given sequence of events, typically a single voyage/shipment. */
export function computeTceForTimeData(
  events: readonly TimeData[],
  /** For order events, only count events for the given shipment (and null). When null, count all events. */
  shipmentId: ClientId | null,
  getIncomeForLoadedAmount: GetIncomeForLoadedAmount,
  distances: Distances,
  options: TceComputationOptions,
  orderById: Map<number, ScenarioOrder> | null,
  pnl: Pnl,
  estimateIdOrScenario: ClientId | 'scenario' = 'scenario',
): {
  breakEvenFreight: number;
  income: number;
  voyageCost: number;
  tcCost: number;
  durationInDays: number;
  seaDurationInDays: number;
  portActivitiesInDays: number;
  earliestStart: number;
  latestEnd: number;
  tce: number;
  loadedAmounts: LoadedAmount[];
  loadedVoyageCharterIds: Set<number>;
  missingIncomeOrderIds: Set<number>;
  totalBunkerCost: number;
  totalChannelCost: number;
  totalExtraCosts: number;
  totalPortCost: number;
  totalVoyageCharterPortCost: number;
  totalEstimatedPortCost: number;
  totalCost: number;
  totalCO2: number;
  fuelEuCost: number;
  euEtsCost: number;
  totalCommissionIncome: number;
  totalFreightIncome: number;
} | null {
  const filteredEvents = excludeDryDockPeriods([...events]);

  // Re-map caller-supplied indices from the original array to the filtered array.
  const filteredIndexByTd = new Map(filteredEvents.map((td, i) => [td, i]));
  const firstTdInFiltered = filteredIndexByTd.get(
    events[options.firstIndex ?? 0],
  );
  const lastTdInFiltered = filteredIndexByTd.get(
    events[options.lastIndex ?? events.length - 1],
  );

  const firstIndex = firstTdInFiltered ?? 0;
  const lastIndex = lastTdInFiltered ?? filteredEvents.length - 1;

  const relevantEvents = filteredEvents.slice(firstIndex, lastIndex + 1);
  const financialBounds = getEventsFinancialBounds(relevantEvents);

  const earliestStart =
    financialBounds.startTime ?? relevantEvents.at(0)?.start;
  const latestEnd = financialBounds.endTime ?? relevantEvents.at(-1)?.end;

  if (!earliestStart || !latestEnd) return null;

  const prevTd = events.at(firstIndex - 1);
  const firstTd = events[firstIndex];
  const lastTd = events[lastIndex];

  const durationInDays =
    financialBounds.startTime && financialBounds.endTime
      ? msToD(financialBounds.endTime - financialBounds.startTime)
      : msToD(lastTd.end - (prevTd?.end ?? firstTd.start));

  const timeData = relevantEvents.filter(
    (td) =>
      !shipmentId ||
      !isCargoEventTimeData(td) ||
      getEventShipmentId(td.event) === shipmentId ||
      getEventShipmentId(td.event) === null,
  );

  const {
    totalCO2,
    totalBunkerCost,
    totalChannelCost,
    totalVoyageCharterPortCost,
    totalEstimatedPortCost,
  } = getTCECosts(
    timeData,
    shipmentId,
    earliestStart,
    latestEnd,
    options.pnlData,
    options.portCallCostSharingDetails,
    options.pnlPortCostByPortCallId,
  );

  const {
    income,
    tcCost,
    missingIncomeOrderIds,
    loadedAmounts,
    loadedVoyageCharterIds,
    totalCommissionIncome,
    totalFreightIncome,
  } = getTDIncomeAndMassDistance(
    timeData,
    getIncomeForLoadedAmount,
    distances,
    undefined,
    estimateIdOrScenario,
  );

  const extraCosts = options.pnlData
    ? getExtraCostsForLoadedAmount(
        loadedAmounts,
        loadedVoyageCharterIds,
        options.pnlData.incomingVoyageChartersById,
        orderById,
      )
    : [];
  const totalExtraCosts = sumBy(extraCosts, (c) => c.value);

  // Calculate sea and port durations
  const seaDurationInDays = sumBy(timeData, (td) => td.hoursAtSea ?? 0) / 24;
  const portActivitiesInDays =
    sumBy(timeData, (td) => td.portActivities ?? 0) / 24;

  // The PnL may carry emission costs for more shipments than the priced events
  // (e.g. a whole-vessel PnL). Scope them to the shipments these events carry
  // so a voyage-level TCE (null shipmentId) sums only this voyage's emission;
  // a specific shipmentId scopes directly.
  const emissionCostInScope = ((): ((
    costShipmentId: ClientId | null,
  ) => boolean) => {
    if (shipmentId) {
      return (costShipmentId) => costShipmentId === shipmentId;
    }

    const voyageShipmentIds = new Set(
      filteredMap(timeData, (td) =>
        isCargoEventTimeData(td)
          ? (getEventShipmentId(td.event) ?? NONE)
          : NONE,
      ),
    );
    return (costShipmentId) =>
      costShipmentId !== null && voyageShipmentIds.has(costShipmentId);
  })();

  const euEtsCost = options.euEtsCostEnabled
    ? sumBy(
        pnl.costs.filter(
          (cost) =>
            cost.category === 'eu_ets_cost' &&
            emissionCostInScope(cost.shipmentId),
        ),
        (euEtsCost) => euEtsCost.amount,
      )
    : 0;

  const fuelEuCost = options.fuelEuCostEnabled
    ? sumBy(
        pnl.costs.filter(
          (cost) =>
            cost.category === 'fuel_eu_cost' &&
            emissionCostInScope(cost.shipmentId),
        ),
        (fuelEuCost) => fuelEuCost.amount,
      )
    : 0;

  const finalBunkerCost = totalBunkerCost;

  // On COA/Spot timelines the executing vessel is the shipowner's, so its
  // operating cost (bunkers, TC hire) is not the charterer's cost — exclude it
  // so the voyage cost reflects only freight and port costs, matching the
  // COA/Spot P&L panel.
  const isContractTimeline =
    options.timelineType === 'coa' || options.timelineType === 'spot';
  const includedBunkerCost = isContractTimeline ? 0 : finalBunkerCost;
  const includedTcCost = isContractTimeline ? 0 : tcCost;

  const voyageCost =
    includedBunkerCost +
    totalChannelCost +
    totalExtraCosts +
    totalVoyageCharterPortCost +
    totalEstimatedPortCost +
    euEtsCost +
    fuelEuCost;

  // Already in the default currency.
  const freightCost = calculateTimelineFreightCosts(
    timeData,
    options.coasById,
    options.pnlData?.worldscaleFreightRates,
    options.shipmentFreightByShipmentClientId,
    options.shipmentExtraFreightsByShipmentClientId,
  );

  const totalCost = voyageCost + includedTcCost + freightCost;
  const tce = calculateTce(income, voyageCost, durationInDays);

  const breakEvenFreight = calculateBreakEvenFreight(
    voyageCost,
    includedTcCost,
    sumBy(loadedAmounts, (la) => la.loadedMass),
  );

  return {
    breakEvenFreight,
    income,
    voyageCost,
    tcCost: includedTcCost,
    durationInDays,
    seaDurationInDays,
    portActivitiesInDays,
    earliestStart,
    latestEnd,
    tce,
    loadedAmounts,
    loadedVoyageCharterIds,
    missingIncomeOrderIds: new Set(missingIncomeOrderIds),
    totalBunkerCost: includedBunkerCost,
    totalChannelCost,
    totalExtraCosts,
    totalPortCost: totalEstimatedPortCost + totalVoyageCharterPortCost,
    totalVoyageCharterPortCost,
    totalEstimatedPortCost,
    totalCost,
    totalCO2,
    euEtsCost,
    fuelEuCost,
    totalCommissionIncome,
    totalFreightIncome,
  };
}

function getIncomingVoyageChartersByPortCall(
  timeData: TimeData[],
  toVisitPortCallId: (portCallId: ClientId) => ClientId,
) {
  const incomingVoyageChartersByPortCallId = new DefaultMap<
    ClientId,
    Set<number>
  >(() => new Set());
  const currentLoadedIncomingVoyageCharterIds: number[] = [];

  timeData.forEach((td) => {
    const incomingVoyageCharterId = isCargoEventTimeData(td)
      ? (td.event.order.incomingVoyageCharterRelation?.voyageCharterId ?? null)
      : null;

    const portCallId = td.event.portCallId;

    if (portCallId) {
      incomingVoyageChartersByPortCallId.update(
        toVisitPortCallId(portCallId),
        (s) => {
          const set = new Set(s);
          currentLoadedIncomingVoyageCharterIds.forEach((vcId) => {
            set.add(vcId);
          });
          return set;
        },
      );
    }

    if (isLoadEventTimeData(td) && incomingVoyageCharterId !== null) {
      currentLoadedIncomingVoyageCharterIds.push(incomingVoyageCharterId);
    }

    if (isDischargeEventTimeData(td) && incomingVoyageCharterId !== null) {
      const targetIndex = currentLoadedIncomingVoyageCharterIds.findIndex(
        (id) => id === incomingVoyageCharterId,
      );
      if (targetIndex > -1) {
        currentLoadedIncomingVoyageCharterIds.splice(targetIndex, 1);
      }
    }
  });

  return incomingVoyageChartersByPortCallId;
}

export function getTCECosts(
  timeData: TimeData[],
  /** When null, count all port calls */
  shipmentId: ClientId | null,
  earliestStart: number,
  latestEnd: number,
  pnlData: PnlData | null,
  portCallCostSharingDetails: CostSharingDetailsByPortCall,
  pnlPortCostByPortCallId?: Map<ClientId, number>,
) {
  let totalCO2 = 0;
  let totalBunkerCost = 0;
  let totalChannelCost = 0;

  // Boundary-pair port calls share one physical visit: resolve the load side
  // to the discharge-side (root) id so estimate cost buckets are keyed once
  // per visit. Identity everywhere else.
  const toVisitPortCallId = createBoundaryVisitResolver(
    timeData.map((td) => td.event),
  ).getVisitRootPortCallId;
  const estimatedPortCostsByPortCall = new DefaultMap<ClientId, number>(
    () => 0,
  );

  timeData.forEach((td, index) => {
    const portCallId = td.event.portCallId;
    const costSharingDetails =
      portCallId !== null
        ? portCallCostSharingDetails.get(portCallId)
        : undefined;

    const isFirstShipmentAtPortCall =
      shipmentId !== null
        ? costSharingDetails?.firstShipmentOnBoard === shipmentId
        : false;

    const isShipmentRelevantAtPortCall =
      shipmentId !== null
        ? costSharingDetails?.shipmentsOnBoard.has(shipmentId) ||
          costSharingDetails?.shipmentsWithCargoOperations.has(shipmentId) ||
          false
        : true;

    const ignoreLegCosts =
      !!shipmentId &&
      ((index === 0 && td.laden) ||
        (!!costSharingDetails?.firstShipmentOnBoard &&
          !isFirstShipmentAtPortCall));
    const ignoreBunkerCosts = td.start < earliestStart || td.start >= latestEnd;

    const currentPortCall = isPortCallEventTimeData(td) ? td : null;

    /* Port calls with no cargo events have shipmentCount 0 */
    const portCallShipmentCount = Math.max(
      costSharingDetails?.shipmentsOnBoard.size ??
        currentPortCall?.event.shipmentCount ??
        1,
      1,
    );

    const { channelCost, bunkerCosts, estimatedPortCosts } =
      countTimeDataTceCosts(
        td,
        portCallShipmentCount,
        ignoreLegCosts,
        ignoreBunkerCosts,
      );

    // On ballast legs on board), channel costs should still be counted — cost sharing doesn't apply and the ballast is part of this voyage.
    const isBallastPortCall =
      !costSharingDetails || costSharingDetails.shipmentsOnBoard.size === 0;
    const effectiveChannelCost =
      ignoreLegCosts && isBallastPortCall ? td.costs.channelCost : channelCost;

    // Include port costs if shipment is on board (including transit/bunkering stops)
    if (portCallId !== null && isShipmentRelevantAtPortCall) {
      estimatedPortCostsByPortCall.update(
        toVisitPortCallId(portCallId),
        (x) => (x ?? 0) + estimatedPortCosts,
      );
    }

    totalCO2 += td.totalCO2;
    totalBunkerCost += bunkerCosts;
    totalChannelCost += effectiveChannelCost;
  });

  const { totalVoyageCharterPortCost, totalEstimatedPortCost } = countPortCosts(
    timeData,
    estimatedPortCostsByPortCall,
    toVisitPortCallId,
    pnlData,
    pnlPortCostByPortCallId,
  );

  return {
    totalCO2,
    totalBunkerCost,
    totalChannelCost,
    totalVoyageCharterPortCost,
    totalEstimatedPortCost,
  };
}

function countTimeDataTceCosts(
  td: TimeData,
  splitAmongCount: number,
  ignoreLegCosts: boolean,
  ignoreBunkerCosts: boolean,
) {
  const bg = td.costs.bunkerGrade;
  const legBunkerCostCombined =
    sumBunkerGrades(bg, 'insideEca').voyageCost +
    sumBunkerGrades(bg, 'outsideEca').voyageCost;
  const idleCost = ignoreBunkerCosts
    ? 0
    : sumBunkerGrades(bg, 'extraConsumption').voyageCost;
  const channelCost = ignoreLegCosts ? 0 : td.costs.channelCost;
  const legBunkerCost = ignoreLegCosts ? 0 : legBunkerCostCombined;
  const portBunkerCost = ignoreBunkerCosts
    ? 0
    : sumBunkerGrades(bg, 'port').voyageCost;

  const portCallCosts = td.costs.portCostEstimate ?? 0;
  const splitCosts = portCallCosts / splitAmongCount;

  return {
    channelCost,
    bunkerCosts: legBunkerCost + portBunkerCost + idleCost,
    estimatedPortCosts: splitCosts,
  };
}

const getVoyageCharterPortCost = (
  pnlData: PnlData | null,
  voyageCharterId: number,
  locationId: LocationId,
) => {
  const vcData = pnlData?.portCostByVcMaps.get(
    `${voyageCharterId}-${locationId}`,
  );

  if (!vcData) return null;

  return convertToDefaultCurrency(
    vcData.cost ?? vcData.estimatedCost ?? 0,
    vcData.currency,
  );
};

/** Counts total voyage charter and estimated port costs. When a port call has a cost from voyage charter, it overrides the estimate.
 * Sum of vc costs and estimated port costs is the total port cost. */
function countPortCosts(
  timeData: TimeData[],
  estimatedPortCostsByPortCall: Map<ClientId, number>,
  toVisitPortCallId: (portCallId: ClientId) => ClientId,
  pnlData: PnlData | null,
  pnlPortCostByPortCallId?: Map<ClientId, number>,
) {
  let totalVoyageCharterPortCost = 0;
  let totalEstimatedPortCost = 0;

  // When PnL port costs are pre-computed, use them directly — they already
  // account for VC costs, priorities, and cost sharing, so the result matches
  // PnL. Walk the timeData (not estimatedPortCostsByPortCall, which may miss
  // port calls where costSharingDetails doesn't track the shipment), reading
  // one bucket per physical visit by the slice's own port call id: a voyage
  // holding one side of a boundary pair reads that side's share, and a slice
  // spanning both sides still counts the visit once (the discharge side — the
  // one holding the original entry and its overrides, like pre-split).
  if (pnlPortCostByPortCallId) {
    const countedVisitIds = new Set<ClientId>();
    for (const td of timeData) {
      const portCallId = td.event.portCallId;
      if (portCallId === null) continue;
      const visitId = toVisitPortCallId(portCallId);
      if (countedVisitIds.has(visitId)) continue;
      const cost = pnlPortCostByPortCallId.get(portCallId);
      if (cost === undefined) continue;
      countedVisitIds.add(visitId);
      totalEstimatedPortCost += cost;
    }
    return { totalVoyageCharterPortCost, totalEstimatedPortCost };
  }

  // Keyed by visit id so a boundary pair resolves to the shared visit bucket
  const locationIdByPortCallId = new Map<ClientId, LocationId>(
    filteredMap(timeData, (td) =>
      td.event.portCallId !== null
        ? [toVisitPortCallId(td.event.portCallId), td.event.locationId]
        : NONE,
    ),
  );

  const incomingVoyageChartersByPortCallId =
    getIncomingVoyageChartersByPortCall(timeData, toVisitPortCallId);

  estimatedPortCostsByPortCall.forEach((estimatedCost, portCallId) => {
    const locationId = locationIdByPortCallId.get(portCallId);
    let ignoreEstimate = false;

    if (locationId !== undefined) {
      const incomingVoyageCharterIds =
        incomingVoyageChartersByPortCallId.get(portCallId);
      // Use the first matching VC port cost for this port call (one cost per port call, not per VC)
      if (incomingVoyageCharterIds) {
        for (const vcId of incomingVoyageCharterIds) {
          const voyageCharterPortCost = getVoyageCharterPortCost(
            pnlData,
            vcId,
            locationId,
          );
          if (voyageCharterPortCost !== null) {
            ignoreEstimate = true;
            totalVoyageCharterPortCost += voyageCharterPortCost;
            break;
          }
        }
      }
    }

    if (!ignoreEstimate) {
      totalEstimatedPortCost += estimatedCost;
    }
  });

  return { totalVoyageCharterPortCost, totalEstimatedPortCost };
}
